/**
 * Home: the A+ dashboard, with the keyboard launcher kept beside it.
 *
 * What the owner sees first -- cash and bank, what customers owe, what is
 * owed to suppliers, the VAT running for the quarter -- then the months'
 * revenue against expenses, what needs attention, how old the
 * receivables are and who owes most.
 *
 * Every financial figure is behind financial reporting rights, and is not
 * even fetched without them: "your clerk does not see it" is a promise
 * the product makes, and hiding a number while still requesting it would
 * leak it over the wire. A clerk or cashier gets the launcher and the
 * recent vouchers they may see.
 *
 * The launcher (every voucher and report with its keycap) stays, because
 * entry is most of the time spent in the app and the fastest route should
 * always be in view.
 */
import { useMemo } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { useQueries, useQuery } from '@tanstack/react-query'
import {
  Box, Button, List, ListItemButton, ListItemText, Paper, Skeleton, Stack,
  Typography,
} from '@mui/material'
import ChevronIcon from '@mui/icons-material/ChevronRightRounded'
import { useTranslation } from 'react-i18next'

import {
  fetchDayBook, fetchLedgerBalances, fetchOutstanding, fetchProfitLoss, fetchTrips,
  fetchVatReturn,
  fetchInventorySummary,
} from '../api/endpoints'
import { BUCKETS, P, type InventorySummary } from '../api/types'
import {
  INVENTORY, isVisible, MASTERS, modulesOf, REPORTS, TRANSPORT, TXN,
} from '../nav/menu'
import { useAuth } from '../store/AuthContext'
import { useGuard } from '../store/UnsavedChanges'
import { useHotkeys } from '../hotkeys/HotkeyProvider'
import { useBi } from '../components/Bi'
import Keycap from '../components/Keycap'
import { c, fmtMoney } from '../theme'
import { today } from '../dates'
import { gregorian } from '../reports/hijri'
import { addMoney, sidedText } from '../vouchers/money'
import {
  cashAndBank, compact, daysBetween, lastMonths, needsAttention, pastDue, pnlTotals,
  previousQuarter, quarterOf, vatDueDate, type Tone,
} from '../dashboard/data'

/*
 * The launcher rows come from the shared menu (nav/menu.ts), which the
 * sidebar and Ctrl+K search also read, so each entry and the permission
 * that hides it is declared once.
 */
const NAV = [...REPORTS, ...MASTERS]

const TONE: Record<Tone, string> = {
  danger: 'var(--danger-ink)', warning: 'var(--warning-ink)', info: 'var(--chart-1)',
}

function Card({ children, sx }: { children: React.ReactNode; sx?: object }) {
  return (
    <Paper variant="outlined" sx={{ p: 2.5, minWidth: 0, ...sx }}>{children}</Paper>
  )
}

function CardTitle({ children, right }: { children: React.ReactNode; right?: React.ReactNode }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center"
      sx={{ mb: 1.5, gap: 1 }}>
      <Typography component="h2" sx={{ m: 0, fontSize: 15, fontWeight: 600 }}>
        {children}
      </Typography>
      {right}
    </Stack>
  )
}

function Kpi({ label, value, sub, loading, to, plain }: {
  label: React.ReactNode; value: string | null; sub?: React.ReactNode
  loading?: boolean; to?: string
  /** A count, not money: no currency. */
  plain?: boolean
}) {
  const guard = useGuard()
  const nav = useNavigate()
  return (
    <Paper variant="outlined" component={to ? 'button' : 'section'}
      onClick={to ? () => guard(() => nav(to)) : undefined}
      sx={{
        p: '18px 20px', textAlign: 'start', font: 'inherit', color: 'inherit',
        cursor: to ? 'pointer' : 'default', display: 'flex', flexDirection: 'column',
        gap: 0.75, minWidth: 0,
        '&:hover': to ? { borderColor: c.borderStrong } : {},
        '&:focus-visible': { outline: '2px solid var(--focus-ring)', outlineOffset: 2 },
      }}>
      <Typography component="div" sx={{ fontSize: 13, fontWeight: 500, color: c.inkMuted }}>
        {label}
      </Typography>
      {loading ? <Skeleton width={140} height={34} /> : (
        <Stack direction="row" alignItems="baseline" spacing={0.75}>
          {!plain && <Typography component="span" sx={{ fontSize: 12, fontWeight: 500, color: c.inkMuted }}>
            SAR</Typography>}
          <Typography component="span" className="num"
            sx={{ fontSize: 24, fontWeight: 600, letterSpacing: '-0.01em', textAlign: 'start' }}>
            {value ?? '—'}
          </Typography>
        </Stack>
      )}
      {sub && (
        <Typography component="div" sx={{ fontSize: 12, color: c.inkMuted,
          borderTop: `1px solid ${c.divider}`, pt: 1, mt: 0.5 }}>
          {sub}
        </Typography>
      )}
    </Paper>
  )
}

export default function HomePage() {
  const nav = useNavigate()
  const guard = useGuard()
  const { t, i18n } = useTranslation()
  const bi = useBi()
  const lang = i18n.language
  const { can, me } = useAuth()

  const txn = TXN.filter((x) => !x.perm || can(x.perm))
  const navItems = NAV.filter((x) => !x.perm || can(x.perm))
  const modules = modulesOf(me)
  const transport = TRANSPORT.filter((x) => isVisible(x, can, modules))
  const inventory = INVENTORY.filter((x) => isVisible(x, can, modules))
  // BizTrans adds its own card: trips run but not yet billed are money
  // the company has earned and not asked for.
  const seesTrips = modules.includes('transport') && can(P.transportView)
  const seesFinancials = can(P.reportFinancial)
  // Inventory adds its own row: stock value against the books, today's
  // sales, and what needs reordering -- each figure from its report.
  const seesStock = modules.includes('inventory') && can(P.inventoryView)
  const seesOperational = can(P.reportOperational)
  const owner = seesFinancials && seesOperational

  const now = new Date()
  const todayIso = today()
  const months = useMemo(() => lastMonths(6, lang), [lang])
  const thisQ = quarterOf(now)
  const lastQ = previousQuarter(thisQ)

  // --- data: financial rights only, or not fetched at all ---------------
  const balances = useQuery({
    enabled: seesFinancials, queryKey: ['ledger-balances'], queryFn: fetchLedgerBalances,
  })
  const recv = useQuery({
    enabled: owner, queryKey: ['outstanding', 'receivable', todayIso, 'dashboard'],
    queryFn: () => fetchOutstanding({ kind: 'receivable', as_of: todayIso, include_bills: false }),
  })
  const pay = useQuery({
    enabled: owner, queryKey: ['outstanding', 'payable', todayIso, 'dashboard'],
    queryFn: () => fetchOutstanding({ kind: 'payable', as_of: todayIso, include_bills: false }),
  })
  const vatNow = useQuery({
    enabled: seesFinancials, queryKey: ['vat-return', thisQ.year, thisQ.quarter],
    queryFn: () => fetchVatReturn({ year: thisQ.year, quarter: thisQ.quarter }),
  })
  const vatLast = useQuery({
    enabled: seesFinancials, queryKey: ['vat-return', lastQ.year, lastQ.quarter],
    queryFn: () => fetchVatReturn({ year: lastQ.year, quarter: lastQ.quarter }),
  })
  const pnl = useQueries({
    queries: months.map((m) => ({
      enabled: seesFinancials,
      queryKey: ['profit-loss', m.from, m.to],
      queryFn: () => fetchProfitLoss({ from_date: m.from, to_date: m.to }),
    })),
  })
  // Everyone: the recent vouchers they are allowed to see.
  const weekAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6)
  const recentFrom = `${weekAgo.getFullYear()}-${String(weekAgo.getMonth() + 1).padStart(2, '0')}-${String(weekAgo.getDate()).padStart(2, '0')}`
  const unbilled = useQuery({
    enabled: seesTrips, queryKey: ['trips', 'unbilled', 'dashboard'],
    queryFn: () => fetchTrips({ status: 'CONFIRMED', unbilled_only: true }),
  })
  const stock = useQuery({
    enabled: seesStock, queryKey: ['inv-summary', todayIso], queryFn: fetchInventorySummary,
  })
  const daybook = useQuery({
    enabled: seesOperational,
    queryKey: ['daybook', recentFrom, todayIso],
    queryFn: () => fetchDayBook({ from_date: recentFrom, to_date: todayIso }),
  })

  useHotkeys('home', [
    ...txn.map((x) => ({
      combo: x.combo!, label: t(x.key),
      handler: () => nav(x.path),
    })),
    ...navItems.map((x) => ({
      combo: x.combo!, label: t(x.key), handler: () => nav(x.path),
    })),
    ...inventory.filter((x) => x.combo).map((x) => ({
      combo: x.combo!, label: t(x.key), handler: () => nav(x.path),
    })),
  ])

  const cash = balances.data ? cashAndBank(balances.data.balances) : null
  const chart = pnl.every((q) => q.data)
    ? months.map((m, i) => ({ ...m, ...pnlTotals(pnl[i].data as Parameters<typeof pnlTotals>[0]) }))
    : null
  const chartMax = chart ? Math.max(1, ...chart.flatMap((m) => [Number(m.income), Number(m.expenses)])) : 1

  const attention = useMemo(() => needsAttention({
    receivable: recv.data, payable: pay.data, balances: balances.data?.balances,
    vat: seesFinancials ? { quarter: lastQ, due: vatDueDate(lastQ), payable: vatLast.data?.vat_due } : undefined,
    today: todayIso,
  }), [recv.data, pay.data, balances.data, vatLast.data, seesFinancials, lastQ.year, lastQ.quarter, todayIso])

  const topCustomers = useMemo(() => [...(recv.data?.parties ?? [])]
    .sort((a, b) => Number(b.total) - Number(a.total)).slice(0, 5), [recv.data])

  const recent = (daybook.data?.entries ?? []).slice(-6).reverse()
  const firstName = (me?.full_name || me?.username || '').split(/\s+/)[0]
  const primary = TXN.find((x) => x.kind === 'receipt' && can(x.perm!))
    ?? TXN.find((x) => x.kind === 'sales' && can(x.perm!)) ?? txn[0]

  const ageingColor: Record<string, string> = {
    current: 'var(--chart-2)', d1_30: 'var(--chart-1)', d31_60: 'var(--warning)',
    d61_90: 'var(--danger-ink)', d90_plus: 'var(--danger-ink)',
  }
  const ageingTotal = recv.data ? Number(recv.data.grand_total) || 1 : 1

  return (
    <Stack spacing={2.5}>
      {/* Header */}
      <Stack direction="row" flexWrap="wrap" alignItems="flex-end" justifyContent="space-between"
        useFlexGap gap={2}>
        <Box>
          <Typography component="h1" sx={{ m: 0, fontSize: 24, fontWeight: 600,
                                            letterSpacing: '-0.01em' }}>
            {bi('dashboard.title')}
          </Typography>
          <Typography sx={{ color: c.inkMuted, mt: 0.5, fontSize: 14 }}>
            {firstName ? t('dashboard.greeting', { name: firstName }) : t('dashboard.greetingAnon')}
          </Typography>
        </Box>
        {primary && (
          <Button variant="contained" onClick={() => nav(primary.path)}>
            {t(primary.key)}
            <Box component="span" className="doc-no" sx={{ ml: 1, fontSize: 11, opacity: 0.8 }}>
              {primary.combo?.replace('alt', 'Alt')}
            </Box>
          </Button>
        )}
      </Stack>

      {seesFinancials && (
        <>
          {/* KPIs */}
          <Box sx={{ display: 'grid', gap: 2,
                     gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))' }}>
            <Kpi label={bi('home.cashBank')} loading={balances.isLoading}
              value={cash ? sidedText(cash.total) : null}
              sub={cash ? t('dashboard.accounts', { count: cash.accounts }) : undefined}
              to="/bank-book" />
            <Kpi label={bi('home.receivables')} loading={recv.isLoading}
              value={recv.data ? fmtMoney(recv.data.grand_total) : null}
              sub={recv.data ? t('dashboard.pastDue', { amount: fmtMoney(pastDue(recv.data)) }) : undefined}
              to="/outstanding" />
            <Kpi label={bi('home.payables')} loading={pay.isLoading}
              value={pay.data ? fmtMoney(pay.data.grand_total) : null}
              sub={pay.data ? t('dashboard.pastDue', { amount: fmtMoney(pastDue(pay.data)) }) : undefined}
              to="/outstanding" />
            <Kpi label={<>{bi('dashboard.vat')} · {t('dashboard.quarterShort', { q: thisQ.quarter })}</>}
              loading={vatNow.isLoading}
              value={vatNow.data ? sidedVat(vatNow.data.vat_due, t) : null}
              sub={t('dashboard.vatDue', { date: gregorian(vatDueDate(thisQ), lang) })}
              to="/vat-return" />
          </Box>

          {/* Chart + attention */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Card sx={{ flex: '2 1 520px' }}>
              <CardTitle right={
                <Stack direction="row" spacing={2} sx={{ fontSize: 12, color: c.inkSecondary }}>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: 'var(--chart-1)' }} />
                    <span>{t('dashboard.revenue')}</span>
                  </Stack>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: 'var(--chart-2)' }} />
                    <span>{t('dashboard.expenses')}</span>
                  </Stack>
                </Stack>
              }>{t('dashboard.revenueVsExpenses')}</CardTitle>
              {!chart ? <Skeleton variant="rectangular" height={190} /> : (
                <>
                  <Box aria-hidden sx={{ display: 'flex', gap: 1.5 }}>
                    <Stack justifyContent="space-between" sx={{ height: 168, fontSize: 11,
                      color: c.inkMuted, textAlign: 'end', minWidth: 32 }}>
                      <span>{compact(chartMax)}</span>
                      <span>{compact(chartMax / 2)}</span>
                      <span>0</span>
                    </Stack>
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'flex-end',
                               justifyContent: 'space-around', height: 168,
                               borderBottom: `1px solid ${c.border}` }}>
                      {chart.map((m) => (
                        <Box key={m.from} sx={{ display: 'flex', alignItems: 'flex-end', gap: '4px',
                                                height: '100%' }}>
                          <Box sx={{ width: 18, borderRadius: '3px 3px 0 0', bgcolor: 'var(--chart-1)',
                                     height: `${(Number(m.income) / chartMax) * 100}%` }} />
                          <Box sx={{ width: 18, borderRadius: '3px 3px 0 0', bgcolor: 'var(--chart-2)',
                                     height: `${(Number(m.expenses) / chartMax) * 100}%` }} />
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  <Box aria-hidden sx={{ display: 'flex', justifyContent: 'space-around',
                                         ml: '44px', mt: 0.75, fontSize: 11.5, color: c.inkMuted }}>
                    {chart.map((m) => <span key={m.from}>{m.label}</span>)}
                  </Box>
                  {/* The same figures for screen readers and for anyone who wants them exact. */}
                  <table className="visually-hidden">
                    <caption>{t('dashboard.revenueVsExpenses')}</caption>
                    <thead><tr><th>{t('dashboard.month')}</th><th>{t('dashboard.revenue')}</th>
                      <th>{t('dashboard.expenses')}</th></tr></thead>
                    <tbody>{chart.map((m) => (
                      <tr key={m.from}><td>{m.label}</td><td>{fmtMoney(m.income)}</td>
                        <td>{fmtMoney(m.expenses)}</td></tr>))}</tbody>
                  </table>
                </>
              )}
            </Card>

            <Card sx={{ flex: '1 1 320px' }}>
              <CardTitle right={attention.length > 0 && (
                <Box component="span" sx={{ fontSize: 12, fontWeight: 600, px: 1, py: 0.25,
                  borderRadius: 999, bgcolor: c.warningSoft, color: c.warningInk }}>
                  {attention.length}</Box>
              )}>{t('dashboard.needsAttention')}</CardTitle>
              {recv.isLoading || balances.isLoading ? <Skeleton height={120} /> :
                attention.length === 0 ? (
                  <Typography sx={{ fontSize: 14, color: c.inkMuted }}>
                    {t('dashboard.nothingNeedsAttention')}</Typography>
                ) : (
                  <Stack spacing={1}>
                    {attention.map((a) => (
                      <Box key={a.key + JSON.stringify(a.values)} component="button" type="button"
                        onClick={() => guard(() => nav(a.path))}
                        sx={{
                          display: 'flex', gap: 1.5, alignItems: 'flex-start', p: '10px 12px',
                          border: `1px solid ${c.divider}`, borderRadius: '8px',
                          bgcolor: c.surface, color: c.ink, font: 'inherit', textAlign: 'start',
                          cursor: 'pointer', width: '100%',
                          '&:hover': { borderColor: c.borderStrong },
                          '&:focus-visible': { outline: '2px solid var(--focus-ring)', outlineOffset: 2 },
                        }}>
                        <Box aria-hidden sx={{ width: 8, height: 8, mt: '6px', borderRadius: '50%',
                                               flex: 'none', bgcolor: TONE[a.tone] }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                            {t(`${a.key}.title`, attnValues(a.values, lang))}</Typography>
                          <Typography sx={{ fontSize: 12, color: c.inkMuted }}>
                            {t(`${a.key}.detail`, attnValues(a.values, lang))}</Typography>
                        </Box>
                        <ChevronIcon fontSize="small" sx={{ color: c.inkMuted, mt: '2px' }} />
                      </Box>
                    ))}
                  </Stack>
                )}
            </Card>
          </Box>

          {/* Ageing + top outstanding */}
          {owner && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Card sx={{ flex: '1 1 320px' }}>
                <CardTitle right={
                  <Button component={RouterLink} to="/outstanding" size="small"
                    sx={{ color: c.primaryInk }}>{t('dashboard.ageingReport')}</Button>
                }>{t('dashboard.receivablesAgeing')}</CardTitle>
                {!recv.data ? <Skeleton height={140} /> : (
                  <Stack spacing={1.5}>
                    {BUCKETS.map((b) => (
                      <Box key={b}>
                        <Stack direction="row" justifyContent="space-between" sx={{ fontSize: 13 }}>
                          <span style={{ color: 'var(--ink-secondary)' }}>{t(`outstanding.${b}`)}</span>
                          <span className="num" style={{ fontWeight: 600 }}>
                            {fmtMoney(recv.data!.totals[b])}</span>
                        </Stack>
                        <Box sx={{ height: 8, borderRadius: 4, bgcolor: c.divider, mt: 0.5 }}>
                          <Box sx={{ height: 8, borderRadius: 4, bgcolor: ageingColor[b],
                            width: `${Math.max(0, Math.min(100,
                              (Number(recv.data!.totals[b]) / ageingTotal) * 100))}%` }} />
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Card>

              <Card sx={{ flex: '1 1 320px' }}>
                <CardTitle>{t('dashboard.topOutstanding')}</CardTitle>
                {!recv.data ? <Skeleton height={140} /> : topCustomers.length === 0 ? (
                  <Typography sx={{ fontSize: 14, color: c.inkMuted }}>
                    {t('dashboard.noOutstanding')}</Typography>
                ) : (
                  <Stack spacing={1.25}>
                    {topCustomers.map((p) => {
                      const name = (lang === 'ar' && p.ledger_name_ar) || p.ledger_name
                      const late = Number(p.buckets.d90_plus) > 0
                      return (
                        <Box key={p.ledger_code} component="button" type="button"
                          onClick={() => guard(() => nav(`/ledger?code=${encodeURIComponent(p.ledger_code)}`))}
                          sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 0, border: 0,
                                bgcolor: 'transparent', font: 'inherit', color: 'inherit',
                                textAlign: 'start', cursor: 'pointer', borderRadius: '6px',
                                '&:focus-visible': { outline: '2px solid var(--focus-ring)' } }}>
                          <Box aria-hidden sx={{ width: 36, height: 36, borderRadius: '8px', flex: 'none',
                            bgcolor: c.primarySoft, color: c.primaryInk, display: 'grid',
                            placeItems: 'center', fontWeight: 600, fontSize: 12 }}>
                            {initials(name)}
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography dir="auto" noWrap sx={{ fontWeight: 500, fontSize: 14,
                                                                 textAlign: 'start' }}>{name}</Typography>
                            <Typography sx={{ fontSize: 12,
                              color: late ? c.dangerInk : c.inkMuted }}>
                              {late ? t('dashboard.over90', { amount: fmtMoney(p.buckets.d90_plus) })
                                    : t('dashboard.pastDueShort', { amount: fmtMoney(p.overdue) })}
                            </Typography>
                          </Box>
                          <Typography className="num" sx={{ fontWeight: 600 }}>{fmtMoney(p.total)}</Typography>
                        </Box>
                      )
                    })}
                  </Stack>
                )}
              </Card>
            </Box>
          )}
        </>
      )}

      {seesStock && <InventoryCards data={stock.data} loading={stock.isLoading} />}

      {seesTrips && <UnbilledTrips data={unbilled.data} loading={unbilled.isLoading}
        today={todayIso} mayInvoice={can(P.transportInvoice)} />}

      {/* Everyone: recent vouchers and the keyboard launcher */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
        {seesOperational && (
          <Card sx={{ flex: '2 1 520px', p: 0 }}>
            <Box sx={{ px: 2.5, pt: 2.5 }}>
              <CardTitle right={<Button component={RouterLink} to="/daybook" size="small"
                sx={{ color: c.primaryInk }}>{t('nav.dayBook')}</Button>}>
                {t('dashboard.recentVouchers')}</CardTitle>
            </Box>
            {daybook.isLoading ? (
              <Box sx={{ p: 2 }}><Skeleton /><Skeleton width="60%" /></Box>
            ) : recent.length ? (
              <Box sx={{ overflowX: 'auto' }}>
                <table className="rpt" style={{ minWidth: 460 }}>
                  <thead><tr>
                    <th>{t('report.voucherNo')}</th>
                    <th>{t('entry.narration')}</th>
                    <th>{t('entry.date')}</th>
                    <th className="num">{t('report.amount')} (SAR)</th>
                  </tr></thead>
                  <tbody>
                    {recent.map((e) => (
                      <tr key={e.voucher_id}>
                        <td><span className="doc-no" style={{ color: 'var(--primary-ink)' }}>
                          {e.voucher_no}</span>
                          <div style={{ fontSize: 12, color: 'var(--ink-muted)' }}>
                            {t(`voucher.${e.voucher_kind.toLowerCase()}`, { defaultValue: e.voucher_kind })}</div>
                        </td>
                        <td>{e.narration ?? ''}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{gregorian(e.date, lang)}</td>
                        <td className="num">{fmtMoney(e.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            ) : (
              <Stack spacing={1.25} sx={{ px: 2.5, pt: 1, pb: 3 }}>
                <Typography variant="body2" color="text.secondary">{t('dashboard.nothingRecent')}</Typography>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  {can(P.postReceipt) && (<>
                    <Keycap combo="alt+6" />
                    <Typography variant="body2">{t('home.emptyReceipt')}</Typography>
                    <Box sx={{ width: 12 }} />
                  </>)}
                  {can(P.postSales) && (<>
                    <Keycap combo="alt+8" />
                    <Typography variant="body2">{t('home.emptySale')}</Typography>
                  </>)}
                </Stack>
              </Stack>
            )}
          </Card>
        )}

        {(txn.length > 0 || navItems.length > 0 || transport.length > 0
          || inventory.length > 0) && (
          // On a phone the bottom bar and the menu are the launcher; a list of
          // keyboard shortcuts there is only a long scroll.
          <Card sx={{ flex: '1 1 320px', p: 0, display: { xs: 'none', sm: 'block' } }}>
            <Box sx={{ px: 2.5, pt: 2.5 }}>
              <CardTitle>{t('dashboard.quickActions')}</CardTitle>
            </Box>
            {[{ key: 'home.transactions', items: txn },
              { key: 'home.reportsMasters', items: navItems },
              { key: 'shell.groupInventory', items: inventory },
              { key: 'home.transport', items: transport }].filter((g) => g.items.length).map((g) => (
              <Box key={g.key} sx={{ pb: 1 }}>
                <Typography sx={{ px: 2.5, pt: 0.5, pb: 0.5, fontSize: 11, fontWeight: 600,
                  letterSpacing: '0.06em', textTransform: 'uppercase', color: c.inkMuted }}>
                  {t(g.key)}</Typography>
                <List dense disablePadding sx={{ display: 'grid', columnGap: 1, px: 1,
                  gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))' }}>
                  {g.items.map((x) => (
                    <ListItemButton key={x.path} className="row-key"
                      onClick={() => nav(x.path)} sx={{ py: 0.5, px: 1, borderRadius: '6px' }}>
                      <ListItemText primary={t(x.key)}
                        primaryTypographyProps={{ fontWeight: 500, fontSize: 14 }} />
                      {x.combo && <Keycap combo={x.combo} />}
                    </ListItemButton>
                  ))}
                </List>
              </Box>
            ))}
          </Card>
        )}
      </Box>
    </Stack>
  )
}

function initials(name: string): string {
  const parts = name.replace(/[^\p{L}\s]/gu, ' ').trim().split(/\s+/).filter(Boolean)
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '·'
}

/** VAT due is payable when positive, refundable when negative. */
function sidedVat(v: string, t: (k: string) => string): string {
  const n = Number(v)
  if (n < 0) return `${fmtMoney(-n)} ${t('dashboard.refund')}`
  return fmtMoney(v)
}

/** Money and dates in an attention line, formatted for display. */
function attnValues(v: Record<string, string | number>, lang: string) {
  const out: Record<string, string | number> = { ...v }
  if (typeof v.amount === 'string' && v.amount) out.amount = fmtMoney(v.amount)
  if (typeof v.due === 'string') out.due = gregorian(v.due, lang)
  if (lang === 'ar' && v.nameAr) out.name = v.nameAr
  return out
}

/** BizTrans on the dashboard: confirmed trips not yet on an invoice. */
function UnbilledTrips({ data, loading, today: todayIso, mayInvoice }: {
  data?: { count: number; trips: { trip_date: string; rate: string }[] }
  loading: boolean; today: string; mayInvoice: boolean
}) {
  const { t } = useTranslation()
  const bi = useBi()
  const nav = useNavigate()
  const guard = useGuard()
  const count = data?.count ?? 0
  const amount = data ? addMoney('0', ...data.trips.map((x) => x.rate)) : '0'
  const oldest = data?.trips.reduce<string | null>(
    (o, x) => (!o || x.trip_date < o ? x.trip_date : o), null)
  const days = oldest ? daysBetween(oldest, todayIso) : 0
  return (
    <Paper variant="outlined" sx={{ p: '16px 20px', display: 'flex', flexWrap: 'wrap',
      alignItems: 'center', gap: 2, borderInlineStart: `3px solid ${
        count && days > 30 ? 'var(--warning)' : 'var(--chart-1)'}` }}>
      <Box sx={{ flex: '1 1 260px', minWidth: 0 }}>
        <Typography component="h2" sx={{ m: 0, fontSize: 15, fontWeight: 600 }}>
          {bi('dashboard.unbilledTrips')}</Typography>
        {loading ? <Skeleton width={220} /> : (
          <Typography sx={{ fontSize: 13, color: c.inkMuted, mt: 0.25 }}>
            {count === 0 ? t('dashboard.unbilledNone')
              : t('dashboard.unbilledDetail', { count, days })}
          </Typography>
        )}
      </Box>
      {!loading && count > 0 && (
        <Stack direction="row" alignItems="baseline" spacing={0.75}>
          <Typography component="span" sx={{ fontSize: 12, color: c.inkMuted }}>SAR</Typography>
          <Typography component="span" className="num" sx={{ fontSize: 22, fontWeight: 600 }}>
            {fmtMoney(amount)}</Typography>
          <Typography component="span" sx={{ fontSize: 12, color: c.inkMuted }}>
            {t('dashboard.exVat')}</Typography>
        </Stack>
      )}
      <Button variant={count && mayInvoice ? 'contained' : 'outlined'}
        onClick={() => guard(() => nav(mayInvoice ? '/transport/invoicing' : '/transport/register'))}>
        {mayInvoice ? t('nav.tripInvoicing') : t('nav.tripRegister')}
      </Button>
    </Paper>
  )
}


function InventoryCards({ data, loading }: { data?: InventorySummary; loading: boolean }) {
  const { t } = useTranslation()
  const bi = useBi()
  const off = data?.difference != null && !data.agrees
  return (
    <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))' }}>
      {(loading || data?.stock_value !== undefined) && (
        <Kpi label={bi('stock.stockValue')} loading={loading}
          value={data?.stock_value !== undefined ? fmtMoney(data.stock_value) : null}
          sub={data?.difference != null ? (
            <span style={{ color: off ? 'var(--warning-ink)' : undefined }}>
              {off ? t('stock.cardDiffers', { diff: fmtMoney(data.difference!) })
                : t('stock.cardAgrees')}</span>) : undefined}
          to="/inventory/stock" />
      )}
      {(loading || data?.sales_today !== undefined) && (
        <Kpi label={bi('stock.salesToday')} loading={loading}
          value={data?.sales_today !== undefined ? fmtMoney(data.sales_today) : null}
          sub={data?.profit_today !== undefined
            ? t('stock.profitToday', { amount: fmtMoney(data.profit_today) }) : t('stock.beforeVat')}
          to="/inventory/sales-by-item" />
      )}
      <Kpi plain label={bi('nav.lowStock')} loading={loading}
        value={data ? String(data.low_stock) : null}
        sub={data ? (data.low_stock ? t('stock.cardReorder', { count: data.low_stock })
          : t('stock.cardNoReorder')) : undefined}
        to="/inventory/reorder" />
    </Box>
  )
}
